<template>
	<ClientOnly>
		<div
			v-if="ad"
			ref="adRoot"
			class="end-of-article end-of-article-ad aizen-end-of-article-ad"
		>
			<div class="related-post-info">
				<h6 class="text-lg leading-none lg:leading-tight">{{ headline }}</h6>
			<a
				v-if="imageUrl"
				:href="trackingLink"
				target="_blank"
				rel="noopener noreferrer"
				@click.passive="onAdClick"
			>
					<img
						width="740"
						height="400"
						:src="imageUrl"
						:alt="headline"
						class="author-post-image-image"
						loading="lazy"
					/>
					<br />
				</a>

				<div v-if="formattedBody">
					<p v-html="formattedBody"></p>
				</div>

				<a
					:href="trackingLink"
					class="author-watchlist-information-subscribe-button"
					target="_blank"
					rel="noopener noreferrer"
				>
					{{ cta }}
				</a>
			</div>
		</div>
	</ClientOnly>
</template>

<script setup>
const props = defineProps({
	slotId: {
		type: String,
		default: "d44329cd-28cb-4a1a-8fd6-5cdd0c1a96bf"
	}
});

const AIZEN_BASE_URL = "https://aizen-ad-manager.up.railway.app";

const ad = useState(`aizen-ad-${props.slotId}`, () => null);
const adLoading = useState(`aizen-ad-loading-${props.slotId}`, () => false);
const impressionTracked = useState(
	`aizen-ad-impression-${props.slotId}`,
	() => false
);

const adRoot = ref(null);

const normalizeAizenUrl = (url) => {
	if (!url) {
		return null;
	}
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	return `${AIZEN_BASE_URL}${url}`;
};

const { pageKey } = usePageKey()
const adCtx = computed(() => ({ pageKey: pageKey.value, surface: "eoa" }))
const trackingLink = computed(() =>
	buildAdClickUrl(
		normalizeAizenUrl(ad.value?.clickUrl) || ad.value?.destinationUrl || "#",
		adCtx.value
	)
);
const onAdClick = () => fireAdClickEvent(adCtx.value)

const imageUrl = computed(() => ad.value?.imageUrl || "");

const headline = computed(() => {
	const properties = ad.value?.properties || {};
	return properties.title || ad.value?.name || "";
});

const body = computed(() => {
	const properties = ad.value?.properties || {};
	return (
		properties.body ||
		properties.Body ||
		properties.description ||
		properties.Description ||
		""
	);
});

// End-of-article body supports [br] and [a href="..."]...[/a] mini-markup.
const formattedBody = computed(() => {
	const raw = body.value;
	if (!raw) {
		return "";
	}
	return raw
		.replace(/\[br\]/g, "<br>")
		.replace(/\[a\s+(.*?)\](.*?)\[\/?a\]/g, "<a $1>$2</a>");
});

const cta = computed(() => {
	const properties = ad.value?.properties || {};
	return properties.cta_label || properties.cta || properties.CTA || "Read more";
});

useAizenImpression(
	adRoot,
	() => normalizeAizenUrl(ad.value?.impressionUrl),
	{ firedRef: impressionTracked }
);

const fetchAd = async () => {
	if (ad.value || adLoading.value) {
		return;
	}

	adLoading.value = true;
	try {
		const response = await $fetch(
			`${AIZEN_BASE_URL}/api/slots/${props.slotId}/serve`
		);

		if (response?.clickUrl || response?.destinationUrl) {
			ad.value = response;
		}
	} catch {
	} finally {
		adLoading.value = false;
	}
};

const scheduleFetch = () => {
	const load = () => {
		fetchAd();
	};

	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(load, { timeout: 2000 });
		return;
	}

	window.setTimeout(load, 0);
};

onMounted(() => {
	if (process.client) {
		scheduleFetch();
	}
});
</script>
